import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import howItWorksData from "../data/howItWorks.json";

function HowItWorks() {
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const testimonials = howItWorksData.testimonials;
  const t = testimonials[testimonialIndex];

  useEffect(() => {
    const interval = setInterval(() => {
      setDirection(1);
      setTestimonialIndex((i) =>
        i === testimonials.length - 1 ? 0 : i + 1
      );
    }, 6000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  const prev = () => {
    setDirection(-1);
    setTestimonialIndex((i) =>
      i === 0 ? testimonials.length - 1 : i - 1
    );
  };
  const next = () => {
    setDirection(1);
    setTestimonialIndex((i) =>
      i === testimonials.length - 1 ? 0 : i + 1
    );
  };

  const variants = {
    enter: (dir) => ({
      x: dir > 0 ? 24 : -24,
      opacity: 0,
      filter: "blur(4px)",
    }),
    center: {
      x: 0,
      opacity: 1,
      filter: "blur(0px)",
      transition: { duration: 0.5, ease: [0.33, 1, 0.68, 1] },
    },
    exit: (dir) => ({
      x: dir < 0 ? 24 : -24,
      opacity: 0,
      filter: "blur(4px)",
      transition: { duration: 0.3 },
    }),
  };

  return (
    <section className="py-16">
      <div className="w-full">
        <div className="rounded-lg border border-[#AC8E66]/20 overflow-hidden shadow-2xl shadow-black/40 font-mono">

          {/* Title Bar */}
          <div className="flex items-center gap-2 px-4 py-2.5 bg-[#1a1a1a] border-b border-[#AC8E66]/10">
            <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
            <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
            <span className="w-3 h-3 rounded-full bg-[#28c840]" />
            <span className="ml-3 text-xs text-[#AC8E66] select-none">
              referenzen
            </span>
            <span className="ml-auto text-xs text-[#EFEBDC]/70 select-none">
              {testimonialIndex + 1}/{testimonials.length}
            </span>
          </div>

          {/* Terminal Content */}
          <div className="px-4 sm:px-6 py-5 bg-[#0F0F0F] min-h-[280px] sm:min-h-[260px] flex flex-col">

            {/* Prompt */}
            <p className="text-sm text-[#EFEBDC]/50 mb-4">
              <span className="text-[#AC8E66]">$</span> cat reviews.log --entry {testimonialIndex + 1}
            </p>

            {/* Animated Quote */}
            <div className="relative flex-1">
              <AnimatePresence initial={false} custom={direction} mode="wait">
                <motion.div
                  key={testimonialIndex}
                  custom={direction}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                >
                  {/* Quote */}
                  <p className="text-sm text-[#EFEBDC]/70 leading-relaxed mb-4">
                    <span className="text-[#AC8E66]/80">&gt;</span>{" "}
                    <span className="text-[#EFEBDC]/50">"</span>
                    {t.quote}
                    <span className="text-[#EFEBDC]/50">"</span>
                  </p>

                  {/* Author Info */}
                  <div className="space-y-1 ml-4">
                    <div className="relative min-h-8 pr-[104px]">
                      <p className="text-xs text-[#EFEBDC]/50 min-w-0 leading-6">
                        <span className="text-[#AC8E66]/40">--</span> {t.name}
                        <span className="text-[#EFEBDC]/25"> @ </span>
                        <span className="text-[#AC8E66]/70">{t.company}</span>
                      </p>
                      {t.image ? (
                        <span
                          className="absolute right-0 top-0 inline-flex items-center justify-center
                        h-8 min-w-[88px] px-2 rounded-md border border-[#AC8E66]/25 bg-[#EFEBDC]"
                        >
                          <img
                            src={t.image}
                            alt={`${t.company} logo`}
                            className="max-h-5 max-w-[84px] object-contain"
                            loading="lazy"
                          />
                        </span>
                      ) : null}
                    </div>
                    <p className="text-xs text-[#EFEBDC]/30">
                      <span className="text-[#AC8E66]/40">--</span> {t.role}
                    </p>
                    <p className="text-xs text-[#AC8E66]/50">
                      <span className="text-[#AC8E66]/40">--</span>{" "}
                      {"★".repeat(t.stars)}
                      {"☆".repeat(5 - t.stars)}
                    </p>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#AC8E66]/10">
              <button
                onClick={prev}
                className="text-xs text-[#EFEBDC]/40 hover:text-[#EFEBDC]/70 transition-colors cursor-pointer"
              >
                <span className="text-[#AC8E66]/40">[</span>prev<span className="text-[#AC8E66]/40">]</span>
              </button>

              {/* Dot Indicators */}
              <div className="flex gap-1.5">
                {testimonials.map((_, i) => (
                  <span
                    key={i}
                    className={`block w-1.5 h-1.5 rounded-full transition-colors ${
                      i === testimonialIndex ? "bg-[#AC8E66]" : "bg-[#EFEBDC]/15"
                    }`}
                  />
                ))}
              </div>

              <button
                onClick={next}
                className="text-xs text-[#EFEBDC]/40 hover:text-[#EFEBDC]/70 transition-colors cursor-pointer"
              >
                <span className="text-[#AC8E66]/40">[</span>next<span className="text-[#AC8E66]/40">]</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default HowItWorks;
